import { useBilling } from "@/lib/hooks/use-billing";
import { netCreditsAvailable } from "@/lib/hooks/use-credit-purchase";
import { isLovablePreview } from "@/lib/utils/environment";
import { useGetWorkspacePreferencesQuery } from "@/lib/graphql/types-and-hooks";
import CreditLimitWarningDialog from "@/components/shared/CreditLimitWarningDialog";

/**
 * Next-interaction hard gate (billing spec: offline overage, NOT opted into
 * auto-reload → "the next time the user interacts with the app, they must buy a
 * credit pack that exceeds the overage before they can proceed").
 *
 * Renders a non-dismissible purchase modal when the workspace is in overage and
 * has not opted into auto-reload (the opted-in case is handled by
 * OverageAutoReload). A successful purchase clears the overage via the
 * normalized cache, which closes the gate.
 *
 * Guarded tightly so it can never lock out spuriously: only when not in
 * preview, billing has loaded, there's a real limit, and net credits across all
 * sources are negative (genuine overage). The prefs query is skipped unless in
 * overage (no always-on cost).
 *
 * Note: the modal requires a pack that covers the overage. An overage larger
 * than the biggest pack (250k credits ≈ 25k offline actions/period) is not
 * self-serviceable — per spec, those customers are contacted directly.
 */
export default function OverageGate() {
  const preview = isLovablePreview();
  const { billingInfo, loading, workspaceId } = useBilling();

  const limit = billingInfo?.totalCreditsLimit ?? 0;
  // Net across all sources (a fully-consumed pack nets 0, not an overage).
  const net = netCreditsAvailable(billingInfo);
  const inOverage = !preview && !loading && limit > 0 && net < 0;

  const { data: prefsData } = useGetWorkspacePreferencesQuery({
    variables: { workspaceId: workspaceId ?? "" },
    skip: preview || !workspaceId || !inOverage,
  });
  const autoRecharge = prefsData?.workspacePreferences?.autoRechargeEnabled ?? false;

  // Gate only the not-opted-in case; opted-in workspaces auto-reload instead.
  const showGate = inOverage && !autoRecharge;
  const overageCredits = Math.max(0, -net);

  return (
    <CreditLimitWarningDialog
      open={showGate}
      onOpenChange={() => {}}
      blocking
      creditsNeeded={overageCredits}
    />
  );
}
